/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import {
  APPLICATION_STATUS_CONTRACT_BASE,
  APPLICATION_STATUS_CONTRACT_DESCRIPTION,
  APPLICATION_STATUS_CONTRACT_DETAILS,
  APPLICATION_STATUS_CONTRACT_DOCUMENTS,
  APPLICATION_STATUS_CONTRACT_INSURANCE,
  APPLICATION_STATUS_CONTRACT_PARTNER,
  APPLICATION_STATUS_CONTRACT_PARTY_TYPE,
  APPLICATION_STATUS_DEBT_COLLECTION,
  APPLICATION_STATUS_DONE,
  APPLICATION_STATUS_POWER_OF_ATTORNEY,
  APPLICATION_STATUS_SUMMARY,
} from "@/src/services/rentalContract/applicationStatus";
import React from "react";
import ProgressBar from "@/src/components/ProgressBar/ProgressBar";
import Insurance from "@/src/components/Form/Common/Insurance/Insurance";
import { getCaseLink } from "@/src/services/productService";
import RentalContractBase from "@/src/components/Form/RentalContract/RentalContractBase";
import progressBarSteps from "./progressBarSteps";
import DebtCollection from "@/src/components/Form/ContractLaw/VehicleContractDetails/DebtCollection";
import { Navigate } from "react-router";
import RentalContractDetails from "@/src/components/Form/RentalContract/RentalContractDetails";
import ContractPartner from "@/src/components/Form/RentalContract/ContractPartner";
import ContractDescription from "@/src/components/Form/RentalContract/ContractDescription";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import Typography from "@mui/material/Typography";
import Summary from "@/src/components/Form/RentalContract/Summary";
import SummaryHintBox from "@/src/components/Form/Common/SummaryHintBox";
import PowerOfAttorney from "@/src/components/Form/RentalContract/PowerOfAttorney";
import { FormSteps } from "@/src/types/FormServiceBase";
import RentalContractContractPartyType from "@/src/components/Form/RentalContract/RentalContractContractPartyType";
import { DocumentsForm } from "@/src/components/Form/DocumentsForm/DocumentsForm";

const formSteps: FormSteps = {
  [APPLICATION_STATUS_CONTRACT_PARTY_TYPE]: {
    title: "Antrag - Ihre Rolle | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_PARTY_TYPE,
    stepUrl: "rolle",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={5} />
        <RentalContractContractPartyType {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_BASE]: {
    title: "Antrag - Ihr Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_BASE,
    stepUrl: "vertrag",
    render: (props: any) => (
      <>
        <HintBox headline={"Wir kümmern uns persönlich um Ihr Anliegen"}>
          <Typography sx={{
            textAlign: "center"
          }}>
            Bitte schildern Sie uns anhand der folgenden Fragen kurz Ihre Situation und suchen Sie falls möglich Ihren Mietvertrag heraus. Jede Information
            hilft uns dabei, Sie bestmöglich zu beraten und direkt rechtliche Schritte einleiten zu können. Sollten Sie bei einer Frage unsicher sein, machen
            Sie sich hierüber keine Sorgen: Sie können zusätzlich den Fall noch frei aus Ihrer Sicht schildern.
          </Typography>
        </HintBox>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={15} />
        <RentalContractBase {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DETAILS]: {
    title: "Antrag - Details zu Ihrem Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DETAILS,
    stepUrl: "vertragsdetails",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={15} />
        <RentalContractDetails {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_PARTNER]: {
    title: "Antrag - Angaben zum Vertragspartner | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_PARTNER,
    stepUrl: "vertragspartner",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={15} />
        <ContractPartner {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_DEBT_COLLECTION]: {
    title: "Antrag - Angaben zum Inkassounternehmen | Legalbird",
    applicationStatus: APPLICATION_STATUS_DEBT_COLLECTION,
    stepUrl: "inkasso",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={15} />
        <DebtCollection {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
    title: "Antrag - Beschreibung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DESCRIPTION,
    stepUrl: "beschreibung",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"contract"} progress={15} />
        <ContractDescription {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
    title: "Antrag - Dokumente | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DOCUMENTS,
    stepUrl: "unterlagen",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"documents"} progress={47} />
        <DocumentsForm product={props.product} submitProps={props.submitProps} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_INSURANCE]: {
    title: "Antrag - Versicherung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_INSURANCE,
    stepUrl: "versicherung",
    render: (props: any) => (
      <>
        <ProgressBar steps={progressBarSteps} active={"insurance"} progress={80} />
        <Insurance {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_SUMMARY]: {
    title: "Antrag - Überprüfen | Legalbird",
    applicationStatus: APPLICATION_STATUS_SUMMARY,
    stepUrl: "ueberpruefen",
    render: (props: any) => (
      <>
        <SummaryHintBox />
        <Summary {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
    title: "Antrag - Vollmacht | Legalbird",
    applicationStatus: APPLICATION_STATUS_POWER_OF_ATTORNEY,
    stepUrl: "vollmacht",
    render: (props: any) => <PowerOfAttorney {...props} />,
  },
  [APPLICATION_STATUS_DONE]: {
    title: "Mein Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_DONE,
    stepUrl: "mein-vertrag",
    render: ({ product }: any) => <Navigate to={getCaseLink(product)} />,
  },
};

export default formSteps;
